import { SelectOption, useNotify, UserPermission } from '@globaleris/ui.library'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumTipoConfiguracion } from '../../api/oData/enums/enumTipoConfiguracion'
import Page from '../../components/page/page'
import PageHeader from '../../components/page/pageHeader'
import TabsWithPermissions, { Tab } from '../../components/tabsWithPermissions/tabsWithPermissions'
import { enumToMapValues, MapToArray } from '../../helpers'
import { hasPermission } from '../../helpers/permissionsHelper'
import { usePageState } from '../../hooks'
import { NonAuthRoutes } from '../../model'
import { Resources } from '../../model/RBAC/constants'
import { storeActiveTabForCurrentPage } from '../../redux'
import { TemplateSettingsTable } from './components/templateSettingsTable'
import { filterTemplatesData, filterWidgetData } from './templateSettings.mapper'
import TemplateSettings, { TemplateSettingsTabs } from './templateSettings.model'
import {  fetchContractTypeListItems, fetchProductsListItems, fetchTemplateSettingsListItems } from './templateSettings.service'

export const TemplateSettingsPage = () => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'templateSettings', 'errorDetailList'])

  const [tableData, setTableData] = useState<TemplateSettings[] | undefined>(undefined)
  const [productsList, setProductsList] = useState<SelectOption[]>([])
  const [contractTypesList, setContractTypesList] = useState<SelectOption[]>([])
  const [currentTab, setCurrentTab] = useState<number>(0)

  const history = useHistory()
  const { info, error } = useNotify()
  const { activeTab } = usePageState()

  //#region Definitions
  const templateSettingsTabsArray = MapToArray(enumToMapValues(TemplateSettingsTabs))

  const tabsDefinition: Map<number, Tab> = new Map(
    templateSettingsTabsArray.map((tab, tabIndex) => [
      tabIndex,
      {
        id: tab.value,
        label: t(`templateSettings:tabs.${tab.name}`),
        hasPermission: hasPermission(Resources.TemplateSettings, [
          UserPermission.EDIT,
          UserPermission.READ_ONLY
        ])
      }
    ])
  )

  //#endregion

  //#region Handlers
  const onTabChangeHandler = (tabIdx: number) => {
    if (currentTab !== tabIdx) {
      setCurrentTab(tabIdx)
      storeActiveTabForCurrentPage(tabIdx)
      // setGlobalNotification(undefined)
    }
  }

  const viewTemplateHandler = (row: TemplateSettings) => {
    row.templateType === EnumTipoConfiguracion.Widget
      ? history.push(NonAuthRoutes.widgetConfiguration, { idElement: row.idDetail })
      : history.push(NonAuthRoutes.templateConfiguration, { idElement: row.idDetail })
  }

  const addTemplateHandler = async () => {
    history.push(NonAuthRoutes.TemplateSettingsAdd, {
      templateType: defineTemplateType(currentTab),
      productsList: productsList
    })
    return true
  }

  const editTemplateHandler = (row: TemplateSettings) => {
    history.push(NonAuthRoutes.TemplateSettingsEdit, {
      productsList: productsList,
      templateSettingsSelected: row
    })
  }

  //#endregion Handlers

  //#region Functions
  const renderTabContent = (activeTab: number) => {
    return (
      templateSettingsTabsArray[activeTab] && (
        <TemplateSettingsTable
          data={defineDataType(activeTab)}
          productsList={productsList}
          contractTypeList={contractTypesList}
          //clientList={clientList}
          templateType={defineTemplateType(currentTab)}
          viewTemplateHandler={viewTemplateHandler}
          addTemplateHandler={addTemplateHandler}
          editTemplateHandler={editTemplateHandler}
        />
      )
    )
  }

  const defineTemplateType = (tabIndex: number): EnumTipoConfiguracion => {
    switch (templateSettingsTabsArray[tabIndex].value) {
      case TemplateSettingsTabs.TEMPLATES:
        return EnumTipoConfiguracion.Plantilla
      default:
        return EnumTipoConfiguracion.Widget
    }
  }

  const defineDataType = (tabIndex: number): TemplateSettings[] | undefined => {
    switch (templateSettingsTabsArray[tabIndex].value) {
      case TemplateSettingsTabs.TEMPLATES:
        return filterTemplatesData(tableData)
      case TemplateSettingsTabs.WIDGETS:
        return filterWidgetData(tableData)
      default:
        return undefined
    }
  }
  //#endregion Functions

  //#region useEffect

  useEffect(() => {
    const getData = async () => {
      await fetchTemplateSettingsListItems().then(response => {
        if (response.success) {
          setTableData(response.data)
        } else {
          error(t('templateSettings:notifications.tableDataError'))
        }
      })
    }

    const getProducts = async () => {
      await fetchProductsListItems().then(response => {
        if (response.success) {
          setProductsList(response.data)
        } else {
          error(t('templateSettings:notifications.productListError'))
        }
      })
    }

    const getContractTypes = async () => {
      await fetchContractTypeListItems().then(response => {
        if (response.success && response.data) {
          setContractTypesList(response.data)
        } else {
          error(t('templateSettings:notifications.contractTypeListError'))
        }
      })
    }

    getContractTypes()
    getProducts()
    getData()
  }, [])

  useEffect(() => {
    if (activeTab !== undefined) {
      setCurrentTab(activeTab)
    } else {
      storeActiveTabForCurrentPage(0)
    }
  }, [activeTab])

  //#endregion

  return (
    <Page>
      <PageHeader title={t('templateSettings:title')} />
      <TabsWithPermissions
        tabs={tabsDefinition}
        tabDefault={currentTab}
        onTabChange={onTabChangeHandler}
      ></TabsWithPermissions>
      {renderTabContent(activeTab ? activeTab : currentTab)}
    </Page>
  )
}
